import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useLocation } from "react-router-dom";

import { NAV_GROUPS } from "@/widgets/app-sidebar/sidebarNav";

import { matchBreadcrumbRoute, type BreadcrumbParent } from "./routes";

const STORAGE_KEY = "tnn:breadcrumb-trail-v1";

export type BreadcrumbCrumb = {
  label: string;
  href?: string;
  /** Section header crumb — opens sibling nav menu. */
  isSection?: boolean;
  sectionGroupId?: string;
};

type StoredTrail = {
  pathname: string;
  crumbs: BreadcrumbCrumb[];
};

type BreadcrumbFromState = { label: string; href: string };

type BreadcrumbContextValue = {
  crumbs: BreadcrumbCrumb[];
  setLabel: (label: string | undefined) => void;
  setParent: (parent: BreadcrumbParent | undefined) => void;
  setSection: (section: string | undefined) => void;
  setHideBreadcrumbs: (hide: boolean) => void;
};

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

function readStored(pathname: string): BreadcrumbCrumb[] | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredTrail;
    if (parsed.pathname === pathname && Array.isArray(parsed.crumbs)) {
      return parsed.crumbs;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeStored(pathname: string, crumbs: BreadcrumbCrumb[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ pathname, crumbs }));
  } catch {
    /* ignore */
  }
}

function buildCrumbs(
  pathname: string,
  labelOverride: string | undefined,
  extraParent: BreadcrumbParent | undefined,
  sectionOverride: string | undefined,
  breadcrumbFrom: BreadcrumbFromState | undefined,
): BreadcrumbCrumb[] {
  const matched = matchBreadcrumbRoute(pathname);
  if (!matched) return [];

  const crumbs: BreadcrumbCrumb[] = [];

  if (matched.def.sectionGroupId) {
    const group = NAV_GROUPS.find((g) => g.id === matched.def.sectionGroupId);
    if (group) {
      crumbs.push({
        label: group.label,
        isSection: true,
        sectionGroupId: group.id,
      });
    }
  }

  if (breadcrumbFrom?.label && breadcrumbFrom.href) {
    crumbs.push({ label: breadcrumbFrom.label, href: breadcrumbFrom.href });
  }

  if (extraParent?.label && extraParent.href) {
    crumbs.push({ label: extraParent.label, href: extraParent.href });
  }

  if (matched.def.parent) {
    crumbs.push({
      label: matched.def.parent.label,
      href: matched.def.parent.href,
    });
  }

  if (sectionOverride) {
    crumbs.push({ label: sectionOverride });
  }

  const leaf =
    (labelOverride && labelOverride.trim()) ||
    (matched.def.dynamicLabel ? matched.def.label : matched.def.label);

  crumbs.push({ label: leaf, href: pathname });

  return crumbs;
}

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;

  const breadcrumbFrom = (location.state as { breadcrumbFrom?: BreadcrumbFromState } | null)
    ?.breadcrumbFrom;

  const [labelOverride, setLabelOverride] = useState<string | undefined>();
  const [extraParent, setExtraParent] = useState<BreadcrumbParent | undefined>();
  const [sectionOverride, setSectionOverride] = useState<string | undefined>();
  const [hideBreadcrumbs, setHideBreadcrumbs] = useState(false);

  useEffect(() => {
    setLabelOverride(undefined);
    setExtraParent(undefined);
    setSectionOverride(undefined);
    setHideBreadcrumbs(false);
  }, [pathname]);

  const crumbs = useMemo(() => {
    if (hideBreadcrumbs) {
      return [];
    }
    const matched = matchBreadcrumbRoute(pathname);
    if (matched?.def.hideBreadcrumb) {
      return [];
    }
    const built = buildCrumbs(
      pathname,
      labelOverride,
      extraParent,
      sectionOverride,
      breadcrumbFrom,
    );
    if (built.length === 0) {
      return readStored(pathname) ?? [];
    }
    return built;
  }, [pathname, labelOverride, extraParent, sectionOverride, breadcrumbFrom, hideBreadcrumbs]);

  useEffect(() => {
    if (crumbs.length > 0) {
      writeStored(pathname, crumbs);
    }
  }, [pathname, crumbs]);

  const setLabel = useCallback((label: string | undefined) => {
    setLabelOverride(label);
  }, []);

  const setParent = useCallback((parent: BreadcrumbParent | undefined) => {
    setExtraParent(parent);
  }, []);

  const setSection = useCallback((section: string | undefined) => {
    setSectionOverride(section);
  }, []);

  const value = useMemo(
    () => ({ crumbs, setLabel, setParent, setSection, setHideBreadcrumbs }),
    [crumbs, setLabel, setParent, setSection],
  );

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbContext(): BreadcrumbContextValue {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    throw new Error("useBreadcrumbContext must be used within BreadcrumbProvider");
  }
  return ctx;
}

export function useBreadcrumbLabel(label: string | undefined) {
  const { setLabel } = useBreadcrumbContext();
  useEffect(() => {
    setLabel(label);
  }, [label, setLabel]);
}

export function useBreadcrumbParent(parent: BreadcrumbParent | undefined) {
  const { setParent } = useBreadcrumbContext();
  useEffect(() => {
    setParent(parent);
  }, [parent, setParent]);
}

export function useBreadcrumbSection(section: string | undefined) {
  const { setSection } = useBreadcrumbContext();
  useEffect(() => {
    setSection(section);
  }, [section, setSection]);
}

/** Hide the layout breadcrumb bar while mounted (e.g. Settings → Features). */
export function useHideBreadcrumbs(hide: boolean) {
  const { setHideBreadcrumbs } = useBreadcrumbContext();
  useEffect(() => {
    setHideBreadcrumbs(hide);
    return () => setHideBreadcrumbs(false);
  }, [hide, setHideBreadcrumbs]);
}
